import { BottomAction } from '@euler/app/components/BottomAction';
import { Typography } from '@euler/app/components/typography';
import { useTaskNavigation } from '@euler/app/flows/task/functions/useTaskNavigation';
import { ConstructionJobProductList } from '@euler/app/flows/task/pages/construction/components/ConstructionJobProductList';
import { WiredTaskDetailPageContainer } from '@euler/app/flows/task/pages/detail/components/DetailPageContainer';
import { useAppLoading } from '@euler/components/loading/Provider';
import { orgMemberToProductAuthor } from '@euler/functions/orgMemberToProductAuthor';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { OrgMember } from '@euler/model/entity';
import { InspectionTaskJobStatus } from '@euler/model/enum';
import { TaskProductManifestItem } from '@euler/model/task-detail';
import { showAlert } from '@euler/utils';
import { memo, useCallback, useRef } from 'react';
import { TextInput, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { useSharedValue } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useConstructionJob } from '../functions/useConstructionJob';
import { ConstructionJobGradientPanel } from './ConstructionJobGradientPanel';
import { ConstructionJobProcedureCell } from './ConstructionJobProcedureCell';
import { WiredConstructionJobResolveList } from './ConstructionJobResolveList';
import { ConstructionJobTechnicianBottomSheetModal } from './ConstructionJobTechnicianBottomSheetModal';
import { ConstructionJobTechnicianPicker } from './ConstructionJobTechnicianPicker';

export const WiredConstructionJobScreen = memo(
  ({ jobId }: { jobId: number }) => {
    const scrollY = useSharedValue(0);
    const insets = useSafeAreaInsets();
    const { rpx } = useResponsiveContext();

    const scrollViewRef = useRef<ScrollView>(null);

    const {
      job,
      jobManager,
      staged,
      dirty,
      editable,
      showActions,
      titleLoading,
      productManifestItems,
      isTechModalVisible,
      onResolveItemPress,
      onTechnicianSelected,
      onTechnicianPickerPress,
      onTechnicianModalDismiss,
      onTakeMedia,
      onDeleteMedia,
      onEditMedia,
      onRetryMediaUpload,
      onPreviewVideo,
      onRemarkTextChange,
      onProcedureMove,
      onRemoveProcedure,
      onSave,
      onFinish,
      onUpdateProductAdoptionConfirmation,
      presentProductAdoptionConfirmation,
    } = useConstructionJob(jobId);

    const onProductManifestChange = useCallback(() => {
      jobManager.dirty$.next(true);
      setTimeout(() => {
        presentProductAdoptionConfirmation();
      }, 250);
    }, [jobManager.dirty$, presentProductAdoptionConfirmation]);

    const navigation = useTaskNavigation();
    const appLoading = useAppLoading();
    const onEditCloser = useCallback(
      (item: TaskProductManifestItem) => {
        navigation.push('_selectCloser', {
          title: '选择成交人',
          onSelect: async (member: OrgMember) => {
            const newCloser = orgMemberToProductAuthor(member);
            navigation.goBack();
            try {
              await jobManager.taskManager.productManifestManager.changeManifestItemCloser(
                item.id,
                newCloser,
              );
              appLoading.show({
                type: 'success',
                message: '已更新',
                duration: 1000,
              });
            } catch (e) {
              console.warn('[construction-job] change-closer failed', e);
              void showAlert('更改成交人失败', '请稍后重试');
            }
          },
        });
      },
      [appLoading, jobManager.taskManager.productManifestManager, navigation],
    );

    return (
      <>
        <WiredTaskDetailPageContainer
          title={staged.name}
          titleLoadingIndicator={titleLoading}
          scrollY={scrollY}
          scrollViewRef={scrollViewRef}
          onShowActions={editable ? showActions : undefined}
          contentContainerStyle={{
            paddingBottom: insets.bottom + rpx(100),
          }}
        >
          <ConstructionJobTechnicianPicker
            technicianName={staged.technicianName}
            onPress={onTechnicianPickerPress}
            editable={editable}
            css={`
              margin-top: ${rpx(15)}px;
              margin-bottom: ${rpx(10)}px;
            `}
          />
          <ConstructionJobProductList
            jobId={jobId}
            subject={staged.name}
            items={productManifestItems ?? []}
            context={staged.context}
            editable={editable}
            onChange={onProductManifestChange}
            onUpdateProductAdoptionConfirmation={
              onUpdateProductAdoptionConfirmation
            }
            onEditCloser={onEditCloser}
            css={`
              margin-top: ${rpx(15)}px;
              background-color: #fff;
              border-radius: ${rpx(8)}px;
              padding-top: 0;
            `}
            contentContainerStyle={{
              paddingHorizontal: rpx(15),
              paddingBottom: rpx(10),
            }}
          />

          {staged.procedures.map((procedure, index) => (
            <ConstructionJobProcedureCell
              key={procedure.name}
              editable={editable}
              index={index}
              first={index === 0}
              last={index === staged.procedures.length - 1}
              procedure={procedure}
              onTakeMedia={onTakeMedia}
              onDeleteMedia={onDeleteMedia}
              onEditMedia={onEditMedia}
              onPreviewVideo={onPreviewVideo}
              onRetryMediaUpload={onRetryMediaUpload}
              onMove={onProcedureMove}
              onRemove={onRemoveProcedure}
              css={`
                margin-top: ${rpx(15)}px;
                background-color: #fff;
                border-radius: ${rpx(8)}px;
              `}
            />
          ))}
          <ConstructionJobGradientPanel
            title="技师备注"
            remark={editable ? '可不填写' : undefined}
            css={`
              margin-top: ${rpx(15)}px;
              margin-bottom: ${rpx(15)}px;
            `}
          >
            {editable ? (
              <TextInput
                multiline
                value={staged.remark ?? ''}
                onChangeText={onRemarkTextChange}
                textAlignVertical="top"
                css={`
                  height: ${rpx(150)}px;
                  border-color: #9bc5f2;
                  border-width: 1px;
                  padding: ${rpx(10)}px;
                  margin: 0 ${rpx(15)}px ${rpx(15)}px ${rpx(15)}px;
                  border-radius: ${rpx(5)}px;
                `}
              />
            ) : (
              <View
                css={`
                  padding: 0 ${rpx(15)}px ${rpx(15)}px ${rpx(15)}px;
                `}
              >
                <Typography.Label
                  numberOfLines={0}
                  light
                  size={rpx(15)}
                  color="#666"
                >
                  {staged.remark ? staged.remark : '暂无备注'}
                </Typography.Label>
              </View>
            )}
          </ConstructionJobGradientPanel>

          <WiredConstructionJobResolveList
            jobName={staged.name}
            resolves={staged.context?.resolved}
            editable={editable}
            onItemPress={editable ? onResolveItemPress : undefined}
            css={`
              margin-bottom: ${rpx(15)}px;
            `}
          />
        </WiredTaskDetailPageContainer>

        <BottomAction scrollY={scrollY}>
          {editable &&
            (job?.status !== InspectionTaskJobStatus.Finished || dirty) && (
              <BottomAction.Button
                variant={
                  job?.status !== InspectionTaskJobStatus.Finished
                    ? 'default'
                    : 'danger'
                }
                text={
                  job?.status === InspectionTaskJobStatus.Finished
                    ? '保存修改'
                    : '保存'
                }
                onPress={onSave}
              />
            )}
          {editable && job?.status !== InspectionTaskJobStatus.Finished && (
            <BottomAction.Button
              variant="primary"
              text="完成施工"
              onPress={onFinish}
              css={`
                margin-left: ${rpx(15)}px;
              `}
            />
          )}
        </BottomAction>

        <ConstructionJobTechnicianBottomSheetModal
          visible={isTechModalVisible}
          technicianId={staged.technicianId}
          onSelect={editable ? onTechnicianSelected : undefined}
          onDismiss={onTechnicianModalDismiss}
        />
      </>
    );
  },
);
